import { Card } from '@nimbus-ds/components';
import { LanguageInterface, LanguagesType } from 'App/i18n';
import { Stack } from 'commons/components';
import TabsMultiLanguage from 'commons/components/TabsMultiLanguage';
import { useAiField } from 'commons/hooks';
import { AI_FIELDS } from 'commons/services/AgentChatService/AgentChatActionBusTypes';
import { mergeObjectsWithContent } from 'commons/utils/mergeObjectsWithContent';
import { useTranslationCatalog } from 'domains/Catalog/hooks';
import FormNameAndDescription, {
  FormNameAndDescriptionProps,
} from './FormNameAndDescription';
import { useNameAndDescriptionAI } from './hooks';

function NameAndDescription({
  name,
  description,
  onChange,
  errors,
  autoFocus,
}: Omit<
  FormNameAndDescriptionProps,
  'isGeneratedAI' | 'setIsFieldGeneratedAI'
>): JSX.Element {
  const t = useTranslationCatalog();
  const missingLanguages = Object.keys(errors)
    .filter((err) => err.startsWith('name'))
    .map((err) => err.split('.')[1]);

  const nameField = useAiField<LanguageInterface>({
    componentId: AI_FIELDS.PRODUCT_NAME,
    getValue: () => name,
    onReplace: (aiGeneratedValue) => {
      onChange({
        name: 'name',
        value: {
          ...name,
          ...aiGeneratedValue,
        },
      });
    },
    onAdd: (aiGeneratedValue) => {
      onChange({
        name: 'name',
        value: mergeObjectsWithContent(name, aiGeneratedValue),
      });
    },
  });

  const descriptionField = useAiField<LanguageInterface>({
    componentId: AI_FIELDS.PRODUCT_DESCRIPTION,
    getValue: () => description ?? {},
    onReplace: (aiGeneratedValue) => {
      onChange({
        name: 'description',
        value: {
          ...description,
          ...aiGeneratedValue,
        },
      });
    },
    onAdd: (aiGeneratedValue) => {
      onChange({
        name: 'description',
        value: mergeObjectsWithContent(description ?? {}, aiGeneratedValue),
      });
    },
  });

  const { isGeneratedAI, setIsFieldGeneratedAI } = useNameAndDescriptionAI();

  return (
    <Card>
      <Card.Header title={t('products.detail.nameAndDescription')} />
      <Card.Body>
        <Stack column spacing="loose" align="stretch">
          <Stack.Item fill>
            <TabsMultiLanguage
              initialLanguage={missingLanguages[0] as LanguagesType}
            >
              <FormNameAndDescription
                onChange={onChange}
                name={name}
                description={description}
                errors={errors}
                autoFocus={autoFocus}
                isGeneratedAI={isGeneratedAI}
                setIsFieldGeneratedAI={setIsFieldGeneratedAI}
                aiNavigationAttributes={{
                  name: nameField.navigationAttributes,
                  description: descriptionField.navigationAttributes,
                }}
              />
            </TabsMultiLanguage>
          </Stack.Item>
        </Stack>
      </Card.Body>
    </Card>
  );
}

export default NameAndDescription;
